import React from 'react';
import {
  Box,
  Typography,
  Theme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  MenuItem,
  Select,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Add, Edit, FileCopy, DeleteOutline, Link as LinkIcon, FilterList, CheckCircle } from '@material-ui/icons';
import Icon from '@mdi/react';
import { mdiQrcode } from '@mdi/js';
import { unpackToDateTime } from '@campfire/hot-date';
import { ConsoleActionButton } from '../../common/ConsoleActionButton';
import { SearchField } from '../../../../../common/inputs/SearchField';
import { TemplateSnackbar, SnackBarOptions } from '../../../../../common/snackbars/TemplateSnackbar';
import { ConfirmationDialog } from '../../../../../common/dialogs/ConfirmationDialog';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { ADMIN_CONSOLE_FUNNELS_QUERY } from './admin-console-funnels.gql';
import { FormFunnelDialog } from './FormFunnelDialog';
import { FunnelQRCodePrintPage } from './FunnelQRCodePrintPage';
import { PublishToSeekDialog, SeekConnection } from './PublishToSeekDialog';
import { SEEK_REGION_LABELS, SeekRegion } from '../Integrations/Apps/Setups/SeekVolunteer/SeekOpportunityFields';
import { ReactComponent as SeekVolunteerIcon } from '../../../../../assets/apps/seek_volunteer_icon.svg';

export interface FunnelProgram {
  programId: string;
  name: string;
}

export interface FunnelActivity {
  activityId: string;
  name: string;
  activityLocation: {
    placesAddress: {
      formatted: string;
    };
  } | null;
}

export interface FunnelActivityRole {
  activityRoleId: string;
  name: string;
}

export interface SeekVolunteerOpportunity {
  seekOpportunityId: string;
  region: SeekRegion;
  datePublished: string;
  categoryId: number | null;
  commitmentId: number | null;
  email: string | null;
  locations: string[];
  isOnlineOrRemote: boolean;
}

export interface AdminConsoleFunnel {
  funnelId: string;
  name: string;
  slug: string;
  description: string | null;
  image: string | null;
  expiresAt: string | null;
  lockPrograms: boolean;
  lockActivities: boolean;
  lockRoles: boolean;
  showTaskList: boolean;
  dateCreated: string;
  isActive: boolean;
  applicationCount: number;
  programs: FunnelProgram[];
  activities: FunnelActivity[];
  activityRoles: FunnelActivityRole[];
  seekVolunteerOpportunities: SeekVolunteerOpportunity[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
    },
    heading: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      paddingBottom: '5px',
      fontSize: 20,
    },
    containerPanel: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    headingPanel: {
      fontWeight: 600,
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    bodyPanel: {
      fontSize: '14px',
      color: '#808080',
    },
    tableHead: {
      '& th': {
        fontWeight: 600,
        fontSize: '11px',
        textTransform: 'uppercase' as const,
        color: theme.color.grey.neutral200,
        borderBottom: '1px solid #e0e0e0',
      },
    },
    funnelName: {
      fontWeight: 600,
      fontSize: '14px',
      color: theme.color.grey.neutralBrand900,
    },
    slugText: {
      color: theme.color.grey.neutral200,
      fontSize: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    lockIndicator: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: 600,
      marginRight: '12px',
    },
    lockActive: {
      backgroundColor: '#e3f2fd',
      color: '#1976d2',
    },
    lockLimited: {
      backgroundColor: '#fff3e0',
      color: '#e65100',
    },
    lockInactive: {
      backgroundColor: '#f5f5f5',
      color: '#bdbdbd',
    },
    actionsCell: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '4px',
    },
    statusFilter: {
      fontSize: '13px',
      paddingTop: '6px',
      paddingBottom: '6px',
      '& .MuiSelect-select': {
        paddingRight: '32px',
      },
    },
  })
);

export function AdminConsoleFunnels() {
  const classes = useStyles();
  const apiUrl = useApiUrl();
  const { isSm } = useCampfireTheme();

  const [searchInput, setSearchInput] = React.useState<string>();
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [showFunnelDialog, setShowFunnelDialog] = React.useState(false);
  const [selectedFunnel, setSelectedFunnel] = React.useState<AdminConsoleFunnel | undefined>();
  const [duplicateFunnel, setDuplicateFunnel] = React.useState<AdminConsoleFunnel | undefined>();
  const [deletingFunnel, setDeletingFunnel] = React.useState<AdminConsoleFunnel | undefined>();
  const [snackbar, setSnackbar] = React.useState<SnackBarOptions>();
  const [publishingFunnel, setPublishingFunnel] = React.useState<AdminConsoleFunnel | undefined>();

  const { data, refetch, loading } = useCampfireQuery<any, {}>(ADMIN_CONSOLE_FUNNELS_QUERY, {});
  const campfireMutation = useCampfireFetch({ defer: true });

  const funnels: AdminConsoleFunnel[] = data?.vm?.funnels ?? [];
  const orgLogo: string | null = data?.orgLogo ?? null;
  const integrationApps: Array<{ name: string }> = data?.wm?.integrationApps ?? [];
  const isSeekVolunteerConnected = integrationApps.some((app) => app.name === 'seek_volunteer');
  const seekConnectionsFetch = useCampfireFetch<{ connections: SeekConnection[] }>({ defer: true });
  const [seekConnections, setSeekConnections] = React.useState<SeekConnection[]>([]);
  const [publishedSeekRegions, setPublishedSeekRegions] = React.useState<Record<string, SeekRegion[]>>({});

  React.useEffect(() => {
    if (!isSeekVolunteerConnected) return;
    seekConnectionsFetch
      .run({ url: `${apiUrl}/wm/integrations/seek-volunteer/manage/get`, method: 'get' })
      .then((response) => setSeekConnections(response.data.data.connections ?? []))
      .catch(() => setSeekConnections([]));
  }, [isSeekVolunteerConnected]);

  const getUnpublishedSeekConnections = (funnel: AdminConsoleFunnel) =>
    seekConnections.filter(
      (connection) =>
        !funnel.seekVolunteerOpportunities.some((opportunity) => opportunity.region === connection.region) &&
        !publishedSeekRegions[funnel.funnelId]?.includes(connection.region)
    );

  const filteredFunnels = React.useMemo(
    () =>
      funnels.filter((funnel) => {
        const matchesSearch =
          !searchInput ||
          funnel.name.toLowerCase().includes(searchInput.toLowerCase()) ||
          funnel.slug.toLowerCase().includes(searchInput.toLowerCase());

        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'active' && funnel.isActive) ||
          (statusFilter === 'expired' && !funnel.isActive);

        return matchesSearch && matchesStatus;
      }),
    [funnels, searchInput, statusFilter]
  );

  const onEdit = (funnel: AdminConsoleFunnel) => {
    setSelectedFunnel(funnel);
    setDuplicateFunnel(undefined);
    setShowFunnelDialog(true);
  };

  const onDuplicate = (funnel: AdminConsoleFunnel) => {
    setSelectedFunnel(undefined);
    setDuplicateFunnel(funnel);
    setShowFunnelDialog(true);
  };

  const onDeleteConfirm = () => {
    if (!deletingFunnel) return;
    campfireMutation
      .run({
        url: `${apiUrl}/vm/funnel/delete`,
        method: 'post',
        data: { funnelId: deletingFunnel.funnelId },
      })
      .then(() => {
        setDeletingFunnel(undefined);
        if (refetch) setTimeout(refetch, 0);
        setSnackbar({ variant: 'success', open: true, message: 'Funnel successfully deleted' });
      })
      .catch((error: any) => {
        setDeletingFunnel(undefined);
        const message = error?.response?.data?.message || 'Failed to delete funnel';
        setSnackbar({ variant: 'error', open: true, message });
      });
  };

  const onCreateNew = () => {
    setSelectedFunnel(undefined);
    setDuplicateFunnel(undefined);
    setShowFunnelDialog(true);
  };

  const onDialogClose = () => {
    setShowFunnelDialog(false);
    setSelectedFunnel(undefined);
    setDuplicateFunnel(undefined);
  };

  const onDialogSuccess = (message: string) => {
    onDialogClose();
    if (refetch) setTimeout(refetch, 0);
    setSnackbar({ variant: 'success', open: true, message });
  };

  const copyLink = (slug: string) => {
    const link = `${window.location.origin}/apply/${slug}`;
    navigator.clipboard.writeText(link);
    setSnackbar({ variant: 'success', open: true, message: 'Link copied to clipboard' });
  };

  const onSeekPublished = (region: SeekRegion) => {
    if (publishingFunnel) {
      const { funnelId } = publishingFunnel;
      setPublishedSeekRegions((prev) => ({ ...prev, [funnelId]: [...(prev[funnelId] ?? []), region] }));
    }
    setPublishingFunnel(undefined);
    if (refetch) setTimeout(refetch, 0);
    setSnackbar({ variant: 'success', open: true, message: 'Funnel published to SEEK Volunteer' });
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const dt = unpackToDateTime(dateStr);
    if (!dt.isValid) return '';
    return dt.toFormat('d MMM y');
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.heading}>
        <Box>
          <Typography variant='h2' className={classes.headingTitle}>
            Funnels
          </Typography>
          <Typography variant='body2'>{'Manage application funnels and conversion tracking'}</Typography>
        </Box>
        <ConsoleActionButton endIcon={<Add />} variant='contained' color='primary' onClick={onCreateNew}>
          New Funnel
        </ConsoleActionButton>
      </Box>

      {loading && <LinearProgress style={{ marginTop: '1rem' }} />}

      {!loading && funnels.length === 0 ? (
        <Box className={classes.containerPanel}>
          <Typography className={classes.headingPanel}>
            <FilterList style={{ fontSize: '14px', paddingRight: '5px' }} /> Create your first funnel
          </Typography>
          <Typography variant='body2' className={classes.bodyPanel}>
            {"You haven't created any funnels yet. Click the 'New Funnel' button top right to add your first."}
          </Typography>
        </Box>
      ) : (
        <Box className={classes.containerPanel}>
          <Box
            display='flex'
            flexDirection={isSm ? 'column' : 'row'}
            alignItems={isSm ? 'left' : 'center'}
            justifyContent='space-between'
            mb='1rem'
          >
            <Box>
              <Typography className={classes.headingPanel}>
                <FilterList style={{ fontSize: '14px', paddingRight: '5px' }} /> Funnels
              </Typography>
              <Typography variant='body2' className={classes.bodyPanel}>
                {`You have ${funnels.length} funnel${
                  funnels.length !== 1 ? 's' : ''
                }. Click 'New Funnel' to create more.`}
              </Typography>
            </Box>
            <Box
              display='flex'
              alignItems='center'
              style={{ marginLeft: isSm ? 0 : '0.75rem', paddingTop: isSm ? '1rem' : 0, gap: '12px' }}
            >
              <SearchField
                placeholder='Search Funnels'
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                fullWidth={isSm}
              />
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as string)}
                disableUnderline
                className={classes.statusFilter}
              >
                <MenuItem value='all'>All Status</MenuItem>
                <MenuItem value='active'>Active</MenuItem>
                <MenuItem value='expired'>Expired</MenuItem>
              </Select>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>Funnel</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Applications</TableCell>
                  <TableCell>Restrictions</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align='right'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFunnels.map((funnel) => (
                  <TableRow key={funnel.funnelId} hover>
                    <TableCell>
                      <Typography className={classes.funnelName}>{funnel.name}</Typography>
                      <Box className={classes.slugText}>
                        <span>/apply/{funnel.slug}</span>
                        <Tooltip title='Copy link'>
                          <IconButton size='small' onClick={() => copyLink(funnel.slug)}>
                            <LinkIcon style={{ fontSize: '14px' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={funnel.isActive ? 'Active' : 'Expired'}
                        size='small'
                        style={{
                          backgroundColor: funnel.isActive ? '#e8f5e9' : '#ffebee',
                          color: funnel.isActive ? '#2e7d32' : '#c62828',
                          fontWeight: 600,
                          fontSize: '11px',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography style={{ fontSize: '13px', fontWeight: 600 }}>{funnel.applicationCount}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box display='flex' flexDirection='row' alignItems='center'>
                        <LockIndicator label='P' locked={funnel.lockPrograms} count={funnel.programs.length} />
                        <LockIndicator label='A' locked={funnel.lockActivities} count={funnel.activities.length} />
                        <LockIndicator label='R' locked={funnel.lockRoles} count={funnel.activityRoles.length} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography style={{ fontSize: '13px' }}>{formatDate(funnel.dateCreated)}</Typography>
                    </TableCell>
                    <TableCell align='right'>
                      <Box className={classes.actionsCell}>
                        {funnel.isActive && getUnpublishedSeekConnections(funnel).length > 0 && (
                          <Tooltip title='Publish to SEEK Volunteer'>
                            <IconButton size='small' onClick={() => setPublishingFunnel(funnel)}>
                              <SeekVolunteerIcon width={18} height={18} style={{ color: '#9e9e9e' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {funnel.seekVolunteerOpportunities.length > 0 && (
                          <Tooltip
                            title={`Published on SEEK Volunteer: ${funnel.seekVolunteerOpportunities
                              .map(
                                (opportunity) =>
                                  `${SEEK_REGION_LABELS[opportunity.region] ?? opportunity.region} (${formatDate(
                                    opportunity.datePublished
                                  )})`
                              )
                              .join(', ')}`}
                          >
                            <Box position='relative' display='inline-flex' p='3px'>
                              <SeekVolunteerIcon width={18} height={18} style={{ color: '#0d3880' }} />
                              <CheckCircle
                                style={{
                                  position: 'absolute',
                                  right: -2,
                                  bottom: -2,
                                  fontSize: 11,
                                  color: '#2e7d32',
                                  backgroundColor: '#fff',
                                  borderRadius: '50%',
                                }}
                              />
                            </Box>
                          </Tooltip>
                        )}
                        {funnel.isActive && (
                          <FunnelQRCodePrintPage
                            slug={funnel.slug}
                            funnelName={funnel.name}
                            description={funnel.description}
                            image={funnel.image}
                            orgLogo={orgLogo}
                            trigger={(onClick) => (
                              <Tooltip title='QR Code'>
                                <IconButton size='small' onClick={onClick}>
                                  <Icon path={mdiQrcode} size={0.75} />
                                </IconButton>
                              </Tooltip>
                            )}
                          />
                        )}
                        <Tooltip title='Edit'>
                          <IconButton size='small' onClick={() => onEdit(funnel)}>
                            <Edit fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Duplicate'>
                          <IconButton size='small' onClick={() => onDuplicate(funnel)}>
                            <FileCopy fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete'>
                          <IconButton size='small' onClick={() => setDeletingFunnel(funnel)}>
                            <DeleteOutline fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            display='flex'
            justifyContent='flex-end'
            alignItems='center'
            style={{ padding: '8px 16px', gap: '20px' }}
          >
            <Box display='flex' alignItems='center'>
              <Box
                width='12px'
                height='12px'
                borderRadius='2px'
                bgcolor='#e3f2fd'
                border='1px solid #1976d2'
                mr='6px'
              />
              <Typography style={{ fontSize: '11px', color: '#616161' }}>Locked</Typography>
            </Box>
            <Box display='flex' alignItems='center'>
              <Box
                width='12px'
                height='12px'
                borderRadius='2px'
                bgcolor='#fff3e0'
                border='1px solid #e65100'
                mr='6px'
              />
              <Typography style={{ fontSize: '11px', color: '#616161' }}>Limited</Typography>
            </Box>
            <Box display='flex' alignItems='center'>
              <Box
                width='12px'
                height='12px'
                borderRadius='2px'
                bgcolor='#f5f5f5'
                border='1px solid #bdbdbd'
                mr='6px'
              />
              <Typography style={{ fontSize: '11px', color: '#616161' }}>Not set</Typography>
            </Box>
          </Box>
        </Box>
      )}

      {showFunnelDialog && (
        <FormFunnelDialog
          funnel={selectedFunnel}
          duplicateFrom={duplicateFunnel}
          onClose={onDialogClose}
          onSuccess={onDialogSuccess}
          onError={(message) => setSnackbar({ variant: 'error', open: true, message })}
        />
      )}

      <ConfirmationDialog
        title='Delete Funnel'
        body={`Are you sure you want to delete "${deletingFunnel?.name}"? This action cannot be undone.`}
        open={Boolean(deletingFunnel)}
        closeActionText='Cancel'
        confirmActionText='Delete'
        handleCloseClick={() => setDeletingFunnel(undefined)}
        handleConfirmClick={onDeleteConfirm}
      />

      {publishingFunnel && (
        <PublishToSeekDialog
          funnel={publishingFunnel}
          connections={getUnpublishedSeekConnections(publishingFunnel)}
          onClose={() => setPublishingFunnel(undefined)}
          onPublished={onSeekPublished}
          onError={(message) => setSnackbar({ variant: 'error', open: true, message })}
        />
      )}

      {snackbar && <TemplateSnackbar {...snackbar} onClose={() => setSnackbar(undefined)} />}
    </Box>
  );
}

function LockIndicator({ label, locked, count }: { label: string; locked: boolean; count: number }) {
  const classes = useStyles();
  const category = label === 'P' ? 'Programs' : label === 'A' ? 'Activities' : 'Roles';
  const hasItems = count > 0;
  const mode = locked && hasItems ? 'locked' : hasItems ? 'limited' : 'none';
  const styleClass =
    mode === 'locked' ? classes.lockActive : mode === 'limited' ? classes.lockLimited : classes.lockInactive;
  const tooltip =
    mode === 'locked'
      ? `${category} locked (${count})`
      : mode === 'limited'
      ? `${category} limited (${count})`
      : `${category} not set`;

  return (
    <Tooltip title={tooltip}>
      <span style={{ position: 'relative' }} className={`${classes.lockIndicator} ${styleClass}`}>
        {label}
        {hasItems && (
          <span
            style={{
              position: 'absolute',
              top: '-6px',
              right: '-6px',
              backgroundColor: mode === 'locked' ? '#1976d2' : '#e65100',
              color: '#fff',
              borderRadius: '50%',
              width: '14px',
              height: '14px',
              fontSize: '9px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            {count}
          </span>
        )}
      </span>
    </Tooltip>
  );
}
